const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const resolveApiUrl = (path: string, base = BASE): string => {
  const normalizedBase = base.replace(/\/$/, '');
  if (path.startsWith('/auth/')) {
    return `${normalizedBase.replace(/\/api\/v\d+$/, '')}${path}`;
  }
  return `${normalizedBase}${path}`;
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

type RequestOptions = {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
};

type FormRequestOptions = {
  method?: Exclude<Method, 'GET'>;
  body: FormData;
  signal?: AbortSignal;
};

const tokenKey = 'canapyra-jwt';

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`${status}: ${body}`);
    this.name = 'ApiError';
  }
}

let onUnauthorized: (() => void) | null = null;

export const setOnUnauthorized = (fn: () => void): void => {
  onUnauthorized = fn;
};

export const setAuthToken = (token: string | null): void => {
  if (token == null) localStorage.removeItem(tokenKey);
  else localStorage.setItem(tokenKey, token);
};

export const apiFetch = async <T>(path: string, opts: RequestOptions = {}): Promise<T> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem(tokenKey);
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(resolveApiUrl(path), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body == null ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) {
    const detail = await res.text();
    throw new ApiError(res.status, detail);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
};

export const apiFetchForm = async <T>(path: string, opts: FormRequestOptions): Promise<T> => {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(tokenKey);
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(resolveApiUrl(path), {
    method: opts.method ?? 'POST',
    headers,
    body: opts.body,
    signal: opts.signal,
  });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
};

export const apiDownload = async (path: string, filename: string): Promise<void> => {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(tokenKey);
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(resolveApiUrl(path), { headers });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'unauthorized');
  }
  if (!res.ok) throw new ApiError(res.status, await res.text());

  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
